/* eslint-disable max-len */
/* eslint-disable */
import { IDependent, INote, IPagination } from 'src/interfaces';
import { getStorageValue } from 'src/hooks/useLocalStorage';
import axios, { AxiosError } from 'axios';
import http from './http';

export interface IEmployeeBaseParams {
  employeeId: string;
  companyId: string;
}

export interface IEmployeeBaseImpersonateParams {
  employeeId: string;
  companyId: string;
  mfaCode?: string;
}

export interface IEmployeeDropdown {
  employeeId: string;
  userId: string;
  firstName: string;
  middleName?: string;
  personnelNumber?: string;
  lastName: string;
  email: string;
}

export interface IEmployeeBaseParamsWithPagination extends IEmployeeBaseParams, IPagination {}

export interface IGetEmployeeServiceParams extends IPagination {
  [x: string]: string | number | boolean | undefined;
  companyId: string;
  employeeStatus: string;
  sortBy?: string;
  sortDirection?: string;
}

export interface GetEmployeeImpersonationServiceParams {
  employeeId: string;
  companyId: string;
}

class EmployeesService {
  static getEmployees(params: IGetEmployeeServiceParams) {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http.get(`employee?${stringParams}`, {
      headers: { companyId: params.companyId },
    });
  }

  static getEmployeesWithUserActive(params: IGetEmployeeServiceParams) {
    let stringParams = '';
    // eslint-disable-next-line no-restricted-syntax
    for (const key in params) {
      if (stringParams === '') {
        stringParams += `${key}=${params[key]}`;
      } else {
        stringParams += `&${key}=${params[key]}`;
      }
    }
    return http.get(`employee/access?${stringParams}`, {
      headers: { companyId: params.companyId },
    });
  }

  static getEmployeeDependents(params: IEmployeeBaseParamsWithPagination) {
    return http.get(`employee-personal/dependents/${params.employeeId}?page=${params.page}&limit=${params.limit}`, {
      headers: { companyId: params.companyId },
    });
  }

  static getEmployeeContacts(params: IEmployeeBaseParamsWithPagination) {
    return http.get(`employee-personal/contacts/${params.employeeId}?page=${params.page}&limit=${params.limit}`, {
      headers: { companyId: params.companyId },
    });
  }

  static getEmployeeNotes(
    params: IEmployeeBaseParamsWithPagination
  ): Promise<{ data: { items: INote[]; meta: { totalItems: number } } }> {
    return http.get(`employee/notes/${params.employeeId}?page=${params.page}&limit=${params.limit}`, {
      headers: { companyId: params.companyId },
    });
  }

  static postEmployeeNote(note: INote, params: IEmployeeBaseParams): Promise<{ id: string }> {
    return http.post(`employee/notes/${params.employeeId}`, note, {
      headers: { companyId: params.companyId },
    });
  }

  static patchEmployeeNote(note: INote, params: IEmployeeBaseParams): Promise<{ id: string }> {
    return http.patch(`employee/notes/${params.employeeId}`, note, {
      headers: { companyId: params.companyId },
    });
  }

  static deleteEmployeeNote(note: INote, params: IEmployeeBaseParams): Promise<{ id: string }> {
    return http.delete(`employee/notes/${params.employeeId}/${note.id}`, {
      headers: { companyId: params.companyId },
    });
  }

  static getEmployeeAuditable(params: IEmployeeBaseParamsWithPagination) {
    return http.get(
      `employee/auditable/${params.employeeId}?page=${params.page}&limit=${params.limit}&sortDirection=${params.sortDirection}&sortBy=${params.sortBy}`,
      {
        headers: { companyId: params.companyId },
      }
    );
  }

  static employeeAuditableReport(params: IEmployeeBaseParams) {
    return http.get(`employee/auditable-report/${params.employeeId}`, {
      headers: { companyId: params.companyId },
    });
  }

  static getEmployeePayStatements(params: IEmployeeBaseParamsWithPagination) {
    return http.get(
      `employee-pay-history/pay-statements/${params.employeeId}?page=${params.page}&limit=${params.limit}`,
      {
        headers: { companyId: params.companyId },
      }
    );
  }

  static postImpersonationLink(body: IEmployeeBaseImpersonateParams): Promise<{ data: string }> {
    return http.post(
      'employee/impt',
      { employeeId: body.employeeId, mfaCode: body.mfaCode },
      {
        headers: { companyId: body.companyId },
      }
    );
  }

  static sendSignupLink(body: IEmployeeBaseParams): Promise<{ data: string }> {
    return http.get(`employee/signup-email/${body.employeeId}`, {
      headers: { companyId: body.companyId },
    });
  }

  static disconnectEmployee(params: IEmployeeBaseParams): Promise<{ data: string }> {
    return http.get(`employee/disconnect/${params.employeeId}`, {
      headers: { companyId: params.companyId },
    });
  }

  static sendResetPasswordLink(params: IEmployeeBaseParams): Promise<{ data: string }> {
    return http.get(`employee/send-reset-password-link/${params.employeeId}`, {
      headers: { companyId: params.companyId },
    });
  }

  static patchEmployeeAccess(params: IEmployeeBaseParams & { permissionId: string }): Promise<{ data: string }> {
    return http.put('employee', params, {
      headers: { companyId: params.companyId },
    });
  }

  static getEmployeeGlanceByCompany(companyId: string): Promise<{ active: number; inactive: number }> {
    return http
      .get('employee/glance', {
        headers: { companyId },
      })
      .then(data => data.data);
  }

  static getEmployeesByDepartment(companyId: string): Promise<[{ department: string; total: number }]> {
    return http
      .get('employee/departments', {
        headers: { companyId },
      })
      .then(data => data.data);
  }

  static postEmployeeDependent(
    dependent: IDependent,
    params: { companyId: string; personnelNumber: string }
  ): Promise<{ id: string }> {
    return http
      .post(`employee-personal/dependents/${params.personnelNumber}`, dependent, {
        headers: { companyId: params.companyId },
      })
      .then(response => response.data)
      .catch((err: Error | AxiosError) => {
        if (axios.isAxiosError(err)) {
          throw new Error(err.response?.data?.message);
        } else {
          return err;
        }
      });
  }

  static getAllEmployeesFromCompany(
    companyId: string,
    showAllEmployee: any,
    employeeStatus: any,
    searchTerm: any
  ): string {
    return `${
      process.env.REACT_APP_API_URL
    }employee/export?showAllEmployee=${showAllEmployee}&search=${searchTerm}&employeeStatus=${employeeStatus}&companyId=${companyId}&token=${getStorageValue(
      'accessToken',
      null
    )}`;
  }

  static getEmployeeDropdown(search: string): Promise<IEmployeeDropdown[]> {
    return http.get(`employee/dropdown?search=${search}`).then(res => res.data);
  }
}
export default EmployeesService;
